/**
 * Phase 17 Plan 17-02 — TanStack Query v5 hooks for KPI management.
 *
 * Query key shapes:
 *   list:   ['kpis', woId, { includeInactive }]
 *   detail: ['kpis', woId, kpiId]
 *
 * All mutations invalidate ['kpis', woId] on success so the list and detail
 * caches are both refreshed.
 *
 * Mirrors useWorkOrderQueries.ts patterns (Phase 16-03).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/error';
import {
  createKpiFromTemplateRequest,
  createKpiRequest,
  deactivateKpiRequest,
  getKpiDetailRequest,
  getKpisForWorkOrderRequest,
  reactivateKpiRequest,
  togglePinKpiRequest,
  updateKpiRequest,
} from '@/services/kpis';
import type {
  CreateKpiBody,
  DeactivateKpiResponse,
  KpiDetail,
  KpiListItem,
  UpdateKpiBody,
} from '@/types/kpi';

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/** Paginated (array) list of KPIs for a work order. */
export function useKpisQuery(woId: number, includeInactive: boolean) {
  return useQuery<KpiListItem[], ApiError>({
    queryKey: ['kpis', woId, { includeInactive }],
    queryFn: () => getKpisForWorkOrderRequest(woId, includeInactive),
    enabled: Number.isFinite(woId) && woId > 0,
    staleTime: 10_000,
  });
}

/** Full KPI detail including applicableMachines + entryCount. */
export function useKpiDetailQuery(woId: number, kpiId: number, enabled = true) {
  return useQuery<KpiDetail, ApiError>({
    queryKey: ['kpis', woId, kpiId],
    queryFn: () => {
      if (!Number.isFinite(woId) || woId <= 0) throw new Error('woId is required');
      if (!Number.isFinite(kpiId) || kpiId <= 0) throw new Error('kpiId is required');
      return getKpiDetailRequest(woId, kpiId);
    },
    enabled: enabled && Number.isFinite(woId) && woId > 0 && Number.isFinite(kpiId) && kpiId > 0,
    staleTime: 5_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

interface CreateKpiVars {
  readonly woId: number;
  readonly body: CreateKpiBody;
}

/** Create a new KPI under a work order. */
export function useCreateKpiMutation() {
  const qc = useQueryClient();
  return useMutation<KpiDetail, ApiError, CreateKpiVars>({
    mutationFn: ({ woId, body }) => createKpiRequest(woId, body),
    onSuccess: (_data, { woId }) => {
      void qc.invalidateQueries({ queryKey: ['kpis', woId] });
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

interface UpdateKpiVars {
  readonly woId: number;
  readonly kpiId: number;
  readonly body: UpdateKpiBody;
}

/** Edit an existing KPI (409 if unit changed with entries). */
export function useUpdateKpiMutation() {
  const qc = useQueryClient();
  return useMutation<KpiDetail, ApiError, UpdateKpiVars>({
    mutationFn: ({ woId, kpiId, body }) => updateKpiRequest(woId, kpiId, body),
    onSuccess: (_data, { woId }) => {
      void qc.invalidateQueries({ queryKey: ['kpis', woId] });
    },
  });
}

interface KpiIdVars {
  readonly woId: number;
  readonly kpiId: number;
}

/** Soft-delete a KPI (isActive=false). */
export function useDeactivateKpiMutation() {
  const qc = useQueryClient();
  return useMutation<DeactivateKpiResponse, ApiError, KpiIdVars>({
    mutationFn: ({ woId, kpiId }) => deactivateKpiRequest(woId, kpiId),
    onSuccess: (_data, { woId }) => {
      void qc.invalidateQueries({ queryKey: ['kpis', woId] });
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

/** Reactivate a soft-deleted KPI (isActive=true). */
export function useReactivateKpiMutation() {
  const qc = useQueryClient();
  return useMutation<KpiDetail, ApiError, KpiIdVars>({
    mutationFn: ({ woId, kpiId }) => reactivateKpiRequest(woId, kpiId),
    onSuccess: (_data, { woId }) => {
      void qc.invalidateQueries({ queryKey: ['kpis', woId] });
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

interface FromTemplateVars {
  readonly woId: number;
  readonly templateId: number;
  readonly overrideMachineIds?: readonly number[];
}

/** Copy a KPI template to a work order (one-way clone, no live link). */
export function useCreateKpiFromTemplateMutation() {
  const qc = useQueryClient();
  return useMutation<KpiDetail, ApiError, FromTemplateVars>({
    mutationFn: ({ woId, templateId, overrideMachineIds }) =>
      createKpiFromTemplateRequest(woId, templateId, {
        ...(overrideMachineIds !== undefined ? { overrideMachineIds } : {}),
      }),
    onSuccess: (_data, { woId }) => {
      void qc.invalidateQueries({ queryKey: ['kpis', woId] });
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Phase 23 (Plan 23-03) — Optimistic pin/unpin mutation (D-239 canonical pattern).
// ---------------------------------------------------------------------------

interface TogglePinVars {
  readonly kpiId: number;
  readonly pinned: boolean;
}

interface TogglePinContext {
  readonly prevKpis?: readonly KpiListItem[];
  readonly prevKpisInactive?: readonly KpiListItem[];
}

/**
 * Phase 23 (Plan 23-03) — D-239 canonical optimistic mutation pattern.
 *
 * Reference for future optimistic mutations. The codebase had ZERO prior
 * `onMutate` / `setQueryData` examples — RESEARCH §4 + PATTERNS §13 verify
 * this is the first introduction.
 *
 * Flow:
 *   1. onMutate cancels any in-flight `['kpis', woId]` and `['dashboard-summary']`
 *      queries (prefix match — the summary key is parameterized by throughput
 *      window per issue #6), snapshots the KPI list caches, and writes the
 *      optimistic flip onto both `includeInactive: false` and
 *      `includeInactive: true` variants. The dashboard cache is never touched
 *      optimistically; the onSettled invalidate refetches the rollup.
 *   2. onError restores the KPI snapshots — pin reverts visually immediately.
 *   3. onSettled invalidates both key prefixes so canonical server data wins.
 *
 * The component renders the star icon's `weight` attr off the cached
 * `isPinnedToDashboard` value, so the optimistic write is what the user sees.
 */
export function useTogglePinKpiMutation(woId: number) {
  const qc = useQueryClient();
  return useMutation<KpiDetail, ApiError, TogglePinVars, TogglePinContext>({
    mutationFn: ({ kpiId, pinned }) => togglePinKpiRequest(woId, kpiId, pinned),
    onMutate: async ({ kpiId, pinned }) => {
      // Cancel in-flight KPI-list fetches so they don't overwrite the
      // optimistic flip, and in-flight dashboard-summary fetches (prefix match
      // across every throughput window) so a stale rollup response doesn't
      // land after the onSettled invalidate.
      await Promise.all([
        qc.cancelQueries({ queryKey: ['kpis', woId] }),
        qc.cancelQueries({ queryKey: ['dashboard-summary'] }),
      ]);

      const prevKpis = qc.getQueryData<readonly KpiListItem[]>([
        'kpis',
        woId,
        { includeInactive: false },
      ]);
      const prevKpisInactive = qc.getQueryData<readonly KpiListItem[]>([
        'kpis',
        woId,
        { includeInactive: true },
      ]);

      const flipMatching = (rows: readonly KpiListItem[] | undefined): KpiListItem[] | undefined =>
        rows?.map((k) => (k.id === kpiId ? { ...k, isPinnedToDashboard: pinned } : k));

      if (prevKpis !== undefined) {
        qc.setQueryData<readonly KpiListItem[]>(
          ['kpis', woId, { includeInactive: false }],
          flipMatching(prevKpis),
        );
      }
      if (prevKpisInactive !== undefined) {
        qc.setQueryData<readonly KpiListItem[]>(
          ['kpis', woId, { includeInactive: true }],
          flipMatching(prevKpisInactive),
        );
      }

      const ctx: TogglePinContext = {
        ...(prevKpis !== undefined ? { prevKpis } : {}),
        ...(prevKpisInactive !== undefined ? { prevKpisInactive } : {}),
      };
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      // Rollback — restore pre-mutation snapshots so the UI flips back to its
      // original state. CONTEXT D-244 demands this is exercised in WP-02.
      if (ctx?.prevKpis !== undefined) {
        qc.setQueryData(['kpis', woId, { includeInactive: false }], ctx.prevKpis);
      }
      if (ctx?.prevKpisInactive !== undefined) {
        qc.setQueryData(['kpis', woId, { includeInactive: true }], ctx.prevKpisInactive);
      }
    },
    onSettled: () => {
      // Always reconcile with the server — this resolves both the success path
      // and the rolled-back error path back to the canonical truth.
      void qc.invalidateQueries({ queryKey: ['kpis', woId] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}
